/**
 * Helper functions for Fleet Overview
 */

import type { Vehicle } from '@/services/vehicles/vehiclesService';
import type { FuelStatus } from '@/types/fleet';

// Formatera fordonets namn
export const getVehicleName = (vehicle: Vehicle): string => {
  if (vehicle.registration_number) {
    return vehicle.registration_number;
  }
  if (vehicle.make && vehicle.model) {
    return `${vehicle.make} ${vehicle.model}`;
  }
  return vehicle.id;
};

// Formatera last_seen
export const formatLastSeen = (lastSeen?: string): string => {
  if (!lastSeen) return '-';
  try {
    const lastSeenDate = new Date(lastSeen);
    const now = new Date();
    const diffMs = now.getTime() - lastSeenDate.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMins / 60);
    const diffDays = Math.floor(diffHours / 24);

    if (diffMins < 1) return 'Just nu';
    if (diffMins < 60) return `${diffMins} min sedan`;
    if (diffHours < 24) return `${diffHours} tim sedan`;
    if (diffDays < 7) return `${diffDays} dag${diffDays > 1 ? 'ar' : ''} sedan`;
    return lastSeenDate.toLocaleString('sv-SE', {
      month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit'
    });
  } catch { return '-'; }
};

// Färgkodning för last_seen
export const getLastSeenColor = (lastSeen?: string): string => {
  if (!lastSeen) return 'text-textTertiary';
  try {
    const diffHours = Math.floor((Date.now() - new Date(lastSeen).getTime()) / 3600000);
    if (diffHours < 1) return 'text-green-600';
    if (diffHours < 24) return 'text-yellow-600';
    return 'text-red-600';
  } catch { return 'text-textTertiary'; }
};

// Rad-bakgrundsfärg baserat på status
export const getRowBackground = (vehicle: Vehicle): string => {
  const status = vehicle.status?.toLowerCase();
  if (status === 'inactive' || status === 'retired' || status === 'inaktiva' || status === 'inaktiv') {
    return 'bg-red-100 hover:bg-red-200';
  }
  if (status !== 'active' && status !== 'aktiv' && status) {
    return 'bg-red-100 hover:bg-red-200';
  }
  return 'bg-gray-50 hover:bg-gray-100';
};

// Motorlampa-status (MIL - Malfunction Indicator Lamp)
export const getMilStatus = (vehicle: Vehicle): 'red' | 'yellow' | 'green' => {
  if (!vehicle.device_imei) return 'yellow';
  return 'green';
};

export const getMilColor = (status: 'red' | 'yellow' | 'green'): string => {
  switch (status) {
    case 'red': return 'text-red-500';
    case 'yellow': return 'text-yellow-500';
    case 'green': return 'text-green-500';
  }
};

export const getMilTitle = (status: 'red' | 'yellow' | 'green', t: (key: string) => string): string => {
  switch (status) {
    case 'red': return t('fleet.milError') || 'Motorfel - lampan tänd';
    case 'yellow': return t('fleet.milNoData') || 'Data ej tillgänglig';
    case 'green': return t('fleet.milOk') || 'Inga fel';
  }
};

// Formatera bränsle/EV-visning: "bränsle% (EV-batteri%)".
// null = parametern rapporteras inte; 0 = ofta ostödd/sovande sensor. Båda visas som
// "–" per del (ingen avläsning) i stället för missvisande "0.00". Saknas båda → "-".
export const formatFuelDisplay = (vehicleId: string, fuelStatus: Record<string, FuelStatus>): string => {
  const status = fuelStatus[vehicleId];
  if (!status) return '-';

  const hasFuel = status.fuelLevel !== null && status.fuelLevel !== 0;
  const hasEv = status.evBatteryCharge !== null && status.evBatteryCharge !== 0;
  if (!hasFuel && !hasEv) return '-';

  const fuel = hasFuel ? status.fuelLevel!.toFixed(2) : '–';
  const ev = hasEv ? status.evBatteryCharge!.toFixed(2) : '–';

  return `${fuel} (${ev})`;
};

// Formatera EV-räckvidd
export const formatEvRange = (vehicleId: string, fuelStatus: Record<string, FuelStatus>): string => {
  const status = fuelStatus[vehicleId];
  // 0 km behandlas som "ingen avläsning" (AVL 755 rapporterar 0 när CAN sover) → visa "–".
  if (!status || status.evRemainingDistance === null || status.evRemainingDistance === 0) return '-';
  return `${status.evRemainingDistance} km`;
};

// Formatera km till service
export const formatServiceDistance = (vehicleId: string, fuelStatus: Record<string, FuelStatus>): string => {
  const status = fuelStatus[vehicleId];
  if (!status || status.distanceUntilService === null) return '-';
  return `${status.distanceUntilService.toLocaleString('sv-SE')} km`;
};

/** Nuvarande mätarställning (DB lagras i meter) */
export const formatCurrentOdometerKm = (meters?: number | null): string => {
  if (meters == null) return '';
  const n = Number(meters);
  if (!Number.isFinite(n)) return '';
  return `${Math.round(n / 1000).toLocaleString('sv-SE')} km`;
};

const FLEET_STATUS_I18N: Record<string, string> = {
  ACTIVE: 'fleet.statusActive',
  MAINTENANCE: 'fleet.statusMaintenance',
  INACTIVE: 'fleet.statusInactive',
  RETIRED: 'fleet.statusRetired',
};

export const getFleetStatusLabelKey = (status?: string | null): string | null => {
  if (!status) return null;
  return FLEET_STATUS_I18N[String(status).trim().toUpperCase()] ?? null;
};
